import appManager from '@app/appManager';
import environment from '@lib/environment';
import { announcementsState } from '@modules/AnnouncementsModule';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import useBrowserState from '@modules/BrowserModule/useBrowserState';
import { useDiagnostics } from '@modules/DiagnosticsModule/DiagnosticsContext';
import { userE911AddressState } from '@modules/E911AddressModule/store';
import { useFeatureFlags } from '@modules/FeatureFlagModule';
import FullStoryModule from '@modules/FullStoryModule';
import { inboxState } from '@modules/InboxModule/state';
import { interactionState } from '@modules/InteractionsModule/state';
import useService from '@modules/ServiceModule/useService';
import { dialerState } from '@modules/SoftPhoneModule/state';
import usePhoneState from '@modules/SoftPhoneModule/usePhoneState';
import useTasks from '@modules/TasksModule/hooks/useTasks';
import { toastsState } from '@modules/ToastModule';
import { userStatusState } from '@modules/UserStatusModule/state';
import { useSnapshot } from 'valtio';

export const useReportData = () => {
  const auth = useAuth();
  const phoneState = usePhoneState();
  const browserState = useBrowserState();
  const {
    userById,
    queueById: _queueId,
    userByContactCenterUserId: _userByContactCenterUserId,
    ...service
  } = useService();
  const tasks = useTasks();
  const ff = useFeatureFlags();
  const { errors, events } = useDiagnostics();
  const logs = appManager.logs.latest;
  const userStatus = useSnapshot(userStatusState);
  const dialer = useSnapshot(dialerState);
  const inbox = useSnapshot(inboxState);
  const interaction = useSnapshot(interactionState);
  const userE911Address = useSnapshot(userE911AddressState);
  const announcements = useSnapshot(announcementsState);
  const toasts = useSnapshot(toastsState);
  // reverse order
  const last10Calls = tasks.tasks
    .slice(-10)
    .map((t) => ({
      from: t.from,
      to: t.to,
      direction: t.direction,
      id: t.id,
      startedAt: t.startedAt.toString(),
      recoveryStartedAt: t.recoveryStartedAt?.toString(),
    }))
    .reverse();

  const data = {
    email: userById?.[auth.user?.id ?? '']?.email ?? '',
    network: auth.serviceName,
    tenants: auth.user?.org.tenants.map((t) => t.name),
    microphoneAccess: browserState.microphoneAccess,
    fullStory: FullStoryModule.sessionUrl,
    last10Calls,
    date: new Date().toISOString(),
    version: environment.APP_VERSION,
    env: environment.RUNTIME_ENV,
    mode: environment.MODE,
    errors: errors.map((e) => ({
      ...e,
      createdAt: new Date(e.createdAt).toISOString(),
    })),
    auth,
    userStatus,
    userE911Address,
    phoneState,
    dialerState: dialer,
    service,
    tasks,
    featureFlags: ff,
    inbox,
    interaction,
    announcements,
    toasts,
    events: events.map((e) => ({
      ...e,
      createdAt: new Date(e.createdAt).toISOString(),
    })),
    logs: logs.map((e) => ({
      ...e,
      createdAt: new Date(e.createdAt).toISOString(),
    })),
  };

  return data;
};
